import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import {
  Box,
  Heading,
  Text,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Divider,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  HStack
} from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { s3Service } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import EnvTable from '../components/EnvTable';
import EnvEditor from '../components/EnvEditor';

// Helper function to determine if a file is an .env file
const isEnvFile = (filename) => {
  return filename.endsWith('.env');
};

// Helper function to determine file type
const getFileType = (filename) => {
  if (filename.endsWith('.env')) return 'env';
  if (filename.endsWith('.php')) return 'php';
  if (filename.endsWith('.htaccess')) return 'htaccess';
  return 'text';
};

function EnvFileViewer() {
  const { fileKey } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isReadOnly } = useContext(AuthContext);
  
  const [fileData, setFileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // User can edit if they're an admin OR they have read-write permission
  const readOnly = isAdmin ? false : isReadOnly;

  useEffect(() => {
    fetchFileData();
  }, [fileKey]);

  const fetchFileData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const decodedKey = decodeURIComponent(fileKey);
      const data = await s3Service.getEnvFile(decodedKey);
      
      setFileData(data);
    } catch (err) {
      console.error('Error loading file:', err);
      setError('Failed to load the .env file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (newContent) => {
    // Update the file data after saving
    const parsedContent = newContent.split('\n')
      .filter(line => line.trim() && !line.startsWith('#'))
      .map(line => {
        const [key, value] = line.split('=');
        return { key: key.trim(), value: value?.trim() || '' };
      });
    
    setFileData(prev => ({
      ...prev,
      raw_content: newContent,
      parsed_content: parsedContent
    }));
  };

  const renderBreadcrumbs = () => {
    if (!fileKey) return null;
    
    const parts = fileKey.split('/').filter(Boolean);
    const fileName = parts.pop(); // Remove the file name
    
    return (
      <Breadcrumb separator={<FaChevronRight color="gray.500" size="0.8em" />} mb={2} fontSize="sm">
        <BreadcrumbItem>
          <BreadcrumbLink onClick={() => navigate('/')} color="inherit">Root</BreadcrumbLink>
        </BreadcrumbItem>
        
        {parts.map((part, index) => {
          const path = parts.slice(0, index + 1).join('/');
          return (
            <BreadcrumbItem key={path}>
              <BreadcrumbLink 
                onClick={() => navigate('/', { state: { initialPath: path } })}
                color="inherit"
              >
                {part}
              </BreadcrumbLink>
            </BreadcrumbItem>
          );
        })}
        
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink color="inherit">{fileName}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
    );
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" />
        <Text mt={4}>Loading .env file...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" rounded="md">
        <AlertIcon />
        {error}
        <Button ml={4} onClick={() => navigate(-1)}>Go Back</Button>
      </Alert>
    );
  }

  return (
    <Box>
      <Box mb={3}>
        <Button 
          leftIcon={<FaChevronLeft />}
          mb={2} 
          onClick={() => navigate(-1)}
          size="sm"
          variant="outline"
        >
          Back
        </Button>
        
        {renderBreadcrumbs()}
        
        <HStack alignItems="center" spacing={2}>
          <Heading as="h1" size="lg">{fileKey.split('/').pop()}</Heading>
          {readOnly && (
            <Text color="gray.500" fontSize="xs" ml={1}>Read-only</Text>
          )}
        </HStack>
      </Box>

      {fileData && (
        <>
          {isEnvFile(fileKey) ? (
            <>
              <Heading as="h2" size="sm" mb={2}>Environment Variables</Heading>
              <EnvTable envData={fileData.parsed_content} readOnly={readOnly} />
              <Divider my={4} />
            </>
          ) : (
            <Box mb={4}>
              <Heading as="h2" size="sm" mb={2}>
                {getFileType(fileKey) === 'php' ? 'PHP File' : 
                 getFileType(fileKey) === 'htaccess' ? '.htaccess File' : 'File Content'}
              </Heading>
            </Box>
          )}

          <EnvEditor 
            fileKey={fileKey}
            initialContent={fileData.raw_content}
            onUpdate={handleUpdate}
            readOnly={readOnly}
            showRawOnly={!isEnvFile(fileKey)}
          />
        </>
      )}
    </Box>
  );
}

export default EnvFileViewer;
